/* @flow */
import idx from 'idx';
import isUuid from 'validator/lib/isUUID';
import * as React from 'react';
import View from 'components/View';
import { graphql } from 'react-apollo';
import Redirect from 'components/Redirect';
import Container from 'components/Container';
import ErrorReporter from 'app/components/ErrorReporter';
import LoadingIndicator from 'components/LoadingIndicator';
import QueryString from 'qs';
import { isEmpty } from 'lodash';

import ORGANIZATION_REDIRECT_QUERY from '../queries/OrganizationRedirect.gql';

import type { OrganizationRedirect as OrganizationRedirectQuery } from '../queries/__generated__/OrganizationRedirect';

type Props = {
  params: { id: string, splat: string }, // splat is what gets captured by the wildcard: * in the URL
  data: ReactApolloData<OrganizationRedirectQuery>,
  location: *,
};

export class OrganizationRedirect extends React.Component<Props> {
  render() {
    const { params, data, location } = this.props;

    const queryString = (() => {
      if (!location || !location.query || isEmpty(location.query)) {
        return '';
      }
      return `?${QueryString.stringify(location.query)}`;
    })();

    if (data.loading) {
      return (
        <Container full>
          <LoadingIndicator align="center" />
        </Container>
      );
    }

    const organization = idx(data, _ => _.organizations[0]);

    if (!organization) {
      return (
        <React.Fragment>
          <ErrorReporter error="OrganizationRedirect - org does not exist" />
          <Redirect to={`/classes${queryString}`} />
        </React.Fragment>
      );
    }

    // if unpaid, then the navigation item was added for upsell purposes.
    if (!organization.paid) {
      return <Redirect to={`/organizations/${organization.id}/features${queryString}`} />;
    }

    // if paid and non admin, the navigation item was added to access broadcast messages.
    const isNonAdmin =
      !organization.userAffiliation.roles.includes('admin') &&
      !organization.userAffiliation.pending_roles.find(pr => pr && pr.role === 'admin');

    if (isNonAdmin) {
      return <Redirect to={`/organizations/${organization.id}/announcements${queryString}`} />;
    }

    // the following is not an after thought,
    // we use organization/:id/some-class-path in marketing emails
    // expecting web to redirect to /classes/:code/some-class-path
    if (organization.class && organization.class.name) {
      let url = `/classes/${organization.class.name}`;
      if (params.splat) {
        url = `${url}/${params.splat}`;
      }
      return <Redirect to={`${url}${queryString}`} />;
    }

    return (
      <ErrorReporter error="OrganizationRedirect - could not redirect user">
        <View>Oops, an error happened: could not load organization page.</View>
      </ErrorReporter>
    );
  }
}

const withOrganization = graphql(ORGANIZATION_REDIRECT_QUERY, {
  options: ({ params }) => {
    if (isUuid(params.id)) {
      return { variables: { organizationUuid: [params.id] } };
    } else {
      return { variables: { organizationId: [params.id] } };
    }
  },
});

export default withOrganization(OrganizationRedirect);
